
  /**
   * @license
   * author: epignosis front-end team
   * @epignosis_llc/gnosis.js v6.0.0
   * Released under the MIT license.
   */

import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import '../../../node_modules/core-js/modules/es.array.concat.js';
import '../../../node_modules/core-js/modules/es.array.filter.js';
import '../../../node_modules/core-js/modules/es.array.map.js';
import '../../../node_modules/core-js/modules/es.object.to-string.js';
import { useState, useRef, useEffect } from 'react';
import SvgArrowRight from '../../icons/chevrons/arrow-right.svg.js';
import SvgArrowLeft from '../../icons/chevrons/arrow-left.svg.js';
import Button from '../Button/Button.js';
import TabsNavItem from './TabsNavItem.js';
import TabsContent from './TabsContent.js';
import { tabsHeader, container } from './styles.js';
import { createElement as jsx } from '../../../node_modules/@emotion/react/dist/emotion-react.browser.esm.js';

var _excluded = ["tabs", "stickyHeader", "selectedTab", "onChangeTab", "inlineEndComponent", "testIds"];
var Tabs = function Tabs(_ref) {
  var _ref$tabs = _ref.tabs,
    tabs = _ref$tabs === void 0 ? [] : _ref$tabs,
    _ref$stickyHeader = _ref.stickyHeader,
    stickyHeader = _ref$stickyHeader === void 0 ? false : _ref$stickyHeader,
    _ref$selectedTab = _ref.selectedTab,
    selectedTab = _ref$selectedTab === void 0 ? 0 : _ref$selectedTab,
    onChangeTab = _ref.onChangeTab,
    inlineEndComponent = _ref.inlineEndComponent,
    testIds = _ref.testIds,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(selectedTab),
    _useState2 = _slicedToArray(_useState, 2),
    activeTab = _useState2[0],
    setActiveTab = _useState2[1];
  var baseTitleRef = useRef(document.title);
  var tabsLength = tabs.length - 1;
  var tabsNavEl = useRef(null);
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isOverflowActive = _useState4[0],
    setIsOverflowActive = _useState4[1];
  var dir = document.dir;
  var onSelectTab = function onSelectTab(index) {
    scrollToTab(index);
    setActiveTab(index);
    onChangeTab && onChangeTab(index);
  };
  var showLeftArrow = function showLeftArrow() {
    if (!isOverflowActive) return false;
    return activeTab > 0;
  };
  var handLeftArrowClick = function handLeftArrowClick() {
    if (activeTab > 0) {
      scrollToTab(activeTab - 1);
      setActiveTab(function (currentTab) {
        return currentTab - 1;
      });
    }
  };
  var showRightArrow = function showRightArrow() {
    if (!isOverflowActive || !tabs.length) return false;
    return activeTab < tabsLength;
  };
  var handRightArrowClick = function handRightArrowClick() {
    if (tabs.length && activeTab < tabsLength) {
      scrollToTab(activeTab + 1);
      setActiveTab(function (currentTab) {
        return currentTab + 1;
      });
    }
  };
  useEffect(function () {
    var newSelectedTab = selectedTab;
    if (selectedTab < 0) {
      newSelectedTab = 0;
    }
    if (selectedTab > tabsLength) {
      newSelectedTab = tabsLength;
    }
    setActiveTab(newSelectedTab);
  }, [selectedTab]);
  useEffect(function () {
    toggleTabsArrow();
  }, [tabsNavEl]);
  var toggleTabsArrow = function toggleTabsArrow() {
    var el = tabsNavEl.current;
    if (el) {
      setIsOverflowActive(el.offsetWidth < el.scrollWidth);
    }
  };

  // add listener on window resize to toggle tabs arrow visibility
  useEffect(function () {
    window.addEventListener("resize", toggleTabsArrow);
    return function () {
      window.removeEventListener("resize", toggleTabsArrow);
    };
  }, []);

  //TODO: find a way to perform scrolling to tab
  var scrollToTab = function scrollToTab(tabIndex) {
    var _document$querySelect;
    var tabOffset = (_document$querySelect = document.querySelector("#tab-".concat(tabIndex))) === null || _document$querySelect === void 0 ? void 0 : _document$querySelect.getBoundingClientRect().x;
    var tablist = document.getElementsByTagName("nav")[0];
    tabOffset && tablist.scrollTo(tabOffset > 0 ? tabOffset - 16 : 0, 0);
  };
  var handleKeyDown = function handleKeyDown(e, index) {
    if (e.key === "ArrowRight" && index < tabsLength) {
      // If next tab is undefined, do nothing
      if (tabs[index + 1].content) {
        onSelectTab(index + 1);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      // If prev tab is undefined, do nothing
      if (tabs[index - 1].content) {
        onSelectTab(index - 1);
      }
    }
  };

  // Restore original title on unmount
  useEffect(function () {
    return function () {
      document.title = baseTitleRef.current;
    };
  }, []);

  // Update document title with the selected tab title for a11y purposes
  useEffect(function () {
    var selected = tabs[activeTab];
    var tabTitle = typeof (selected === null || selected === void 0 ? void 0 : selected.title) === "string" ? selected.title : "";
    var newTitle = tabTitle ? "".concat(baseTitleRef.current, " | ").concat(tabTitle) : baseTitleRef.current;
    if (document.title !== newTitle) document.title = newTitle;
  }, [activeTab, tabs]);
  return jsx("section", _extends({
    css: container
  }, rest), jsx("div", {
    className: "nav-wrapper",
    role: "navigation",
    tabIndex: 0
  }, showLeftArrow() && jsx(Button, {
    "data-testid": "left-arrow",
    variant: "link",
    color: "secondary",
    noGutters: true,
    onClick: handLeftArrowClick
  }, dir === "rtl" ? jsx(SvgArrowRight, {
    height: 22
  }) : jsx(SvgArrowLeft, {
    height: 22
  })), jsx("nav", _extends({
    css: function css(theme) {
      return tabsHeader(theme, {
        stickyHeader: stickyHeader
      });
    },
    role: "tablist",
    ref: tabsNavEl
  }, (testIds === null || testIds === void 0 ? void 0 : testIds.nav) && {
    "data-testid": testIds.nav
  }), tabs.filter(function (tab) {
    return tab.content;
  }).map(function (_ref2, index) {
    var title = _ref2.title,
      id = _ref2.id;
    return jsx(TabsNavItem, {
      id: id,
      key: index,
      index: index,
      title: title,
      isActive: activeTab === index,
      onSelectTab: onSelectTab,
      onKeyDown: function onKeyDown(e) {
        return handleKeyDown(e, index);
      }
    });
  }), inlineEndComponent && jsx("div", {
    className: "inline-end-component"
  }, inlineEndComponent)), showRightArrow() && jsx(Button, {
    "data-testid": "right-arrow",
    variant: "link",
    color: "secondary",
    noGutters: true,
    onClick: handRightArrowClick
  }, dir === "rtl" ? jsx(SvgArrowLeft, {
    height: 22
  }) : jsx(SvgArrowRight, {
    height: 22
  }))), jsx("div", _extends({
    id: "content",
    "aria-live": "polite",
    role: "region"
  }, (testIds === null || testIds === void 0 ? void 0 : testIds.content) && {
    "data-testid": testIds.content
  }), tabs.filter(function (tab) {
    return tab.content;
  }).map(function (_ref3, index) {
    var content = _ref3.content;
    return jsx(TabsContent, {
      key: index,
      index: index,
      content: content,
      isVisible: activeTab === index
    });
  })));
};

export { Tabs as default };
//# sourceMappingURL=Tabs.js.map
